import { util } from 'echarts/core';
import type { Option } from '../../../core/option';
import type StandardChart from '../../../core/StandardChart';
import { type ThemeParser } from '../../../core/theme';
import { normalizeToArray } from '../../../util/utils';
import { dataZoomThemeParse } from '../component/dataZoom';
import { getLineColor } from '../../mobile/static/color';
import { parseGrid } from '../component/grid';
import { parsePCXAxisOption, parseXAxisLine } from '../component/xAxis';
import { parsePCYAxisLayout, parsePCYAxisStyle } from '../component/yAxis';

type Density = 'highDensity' | 'lowDensity';
type LineSeriesOption = Option['series'] & {
  type: 'line';
  dvMaximumLabel: true;
  dvDensity: Density;
};

function getDensityCompress(width: number, count: number): Density {
  // 单个点对应的宽度的阈值，低于阈值则为高密度。
  const pointWidthCompress = 20;
  return width / count < pointWidthCompress ? 'highDensity' : 'lowDensity';
}
function parseLineMaximumLabel(seriesOptions: LineSeriesOption[]) {
  seriesOptions.forEach(series => {
    if (!series.dvMaximumLabel) {
      return;
    }
    series?.markPoint?.data.push(...[{ type: 'max' }, { type: 'min' }]);
  });
}
/**
 * 需要根据页面给定的绘制区域确定高低密度。
 * @param seriesOption
 */
function parsePCLineSeriesOption(seriesOptions: LineSeriesOption[], chart: StandardChart) {
  // 折线的绘制区域需要确定y轴标签的布局方式和占用的宽度，
  // 但是在绘制之前获取不到宽度，所以不能确定折线的绘制区域的宽度。
  const chartWidth = chart.getDom().getBoundingClientRect().width;
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';
  seriesOptions.forEach((series, index) => {
    const dataLen = series.data.length;
    // 折线的密度阈值
    const density: Density = getDensityCompress(chartWidth, dataLen);

    // 根据PC静态范式规范，创建默认的配置项集合
    const defaultSymbolSize = 4.5;
    const defaultLineSeries: LineSeriesOption = {
      type: 'line',
      dvDensity: density,
      symbol: 'emptyCircle',
      symbolSize: density === 'lowDensity' ? defaultSymbolSize : 0,
      dvEmphasisSymbolFill: themeMode === 'light' ? '#fff' : '#000',
      itemStyle: {
        color: getLineColor(seriesOptions.length, index)
      },
      lineStyle: {
        width: 1
      },
      emphasis: {
        scale: false
      }
    };
    util.merge(series, defaultLineSeries);
  });
}

export function pcLineThemeParse(...args: Parameters<ThemeParser>): ReturnType<ThemeParser> {
  const [option, chart] = args;
  const series = normalizeToArray(option.series);
  const lineOptions = series.filter(opt => opt.type === 'line') as LineSeriesOption[];
  if (lineOptions.length === 0) {
    return;
  }
  //component
  parseGrid(option);
  parseXAxisLine(option);
  parsePCXAxisOption(option, chart);
  dataZoomThemeParse(option, chart);
  parsePCYAxisLayout(option, chart);
  parsePCYAxisStyle(option, chart);
  // series
  parsePCLineSeriesOption(lineOptions, chart);
  parseLineMaximumLabel(lineOptions, chart);
}
